"use client";

import type { LyricsWithSlug } from "@/types";
import LoopIcon from "@mui/icons-material/Loop";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useRef, useState } from "react";
import { AnalyticsEvents, trackEvent } from "~/connectors/analytics.connector";

const YoutubePlayer = ({ lyrics }: { lyrics: LyricsWithSlug }) => {
  // Wrapper ensures hooks in the inner component are called unconditionally
  if (!lyrics?.youtube) return null;
  const videoId = lyrics.youtube?.id;
  if (!videoId) return null;

  return <YoutubePlayerInner lyrics={lyrics} />;
};

const YoutubePlayerInner = ({ lyrics }: { lyrics: LyricsWithSlug }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const [isIframeVisible, setIsIframeVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<any>(null);

  const videoId = lyrics.youtube!.id;
  const thumbnail = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  useYouTubePlayer(isPlaying, videoId, containerRef, playerRef, lyrics.name, isLooping, setIsPlaying, setIsIframeVisible);

  return (
    <Box sx={{ width: '100%', my: 2, px: { xs: 0, sm: 0 } }}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          pt: isPlaying ? '56.25%' : { xs: '36%', sm: '18%' }, // 16:9 when playing, much smaller when not
          borderRadius: 1,
          overflow: 'hidden',
          boxShadow: 1,
          transition: 'padding-top 380ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      >
        {/* Always keep the player container mounted to avoid DOM double-removal
            YT.Player will manage iframe children; we overlay the thumbnail when not playing */}
        <div
          ref={containerRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        />

        {/* Allow iframe to receive pointer events directly so YouTube's native
            hover and control interactions continue to work as expected. */}

        {/* keep overlay mounted so we can fade it instead of removing immediately */}
        <Box
          role="button"
          tabIndex={isIframeVisible ? -1 : 0}
          aria-label={`Play ${lyrics.name}`}
          onClick={() => !isPlaying && setIsPlaying(true)}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (isIframeVisible) return;
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
              e.preventDefault();
              setIsPlaying(true);
            }
          }}
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: '#000',
            backgroundImage: `url(${thumbnail})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'opacity 300ms ease, visibility 300ms',
            // keep thumbnail visible until iframe is actually playing
            opacity: isIframeVisible ? 0 : 1,
            visibility: isIframeVisible ? 'hidden' : 'visible',
            pointerEvents: isIframeVisible ? 'none' : 'auto',
            cursor: isIframeVisible ? 'default' : 'pointer',
            // when hovering/focusing the overlay, make the inner IconButton show hover styles
            '&:hover .yt-play-button, &:focus .yt-play-button': {
              backgroundColor: 'rgba(0,0,0,0.7)',
              transform: 'scale(1.02)',
            },
          }}
        >
          <IconButton
            className="yt-play-button"
            onClick={() => setIsPlaying(true)}
            aria-hidden={true}
            size="large"
            sx={{
              backgroundColor: 'rgba(0,0,0,0.6)',
              color: 'common.white',
              width: { xs: 56, sm: 72 },
              height: { xs: 56, sm: 72 },
              boxShadow: 3,
              transition: 'transform 220ms, background-color 220ms, opacity 220ms',
              position: 'relative',
              overflow: 'visible',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
            }}
          >
            {/* crossfade Play -> Loader */}
            <Box
              component="span"
              className="yt-play-icon"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'opacity 220ms, transform 220ms',
                opacity: isPlaying && !isIframeVisible ? 0 : 1,
                transform: isPlaying && !isIframeVisible ? 'scale(0.92)' : 'scale(1)',
              }}
            >
              <PlayArrow sx={{ fontSize: { xs: 28, sm: 42 } }} />
            </Box>

            <Box
              component="span"
              className="yt-loader"
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'opacity 220ms',
                opacity: isPlaying && !isIframeVisible ? 1 : 0,
                pointerEvents: 'none',
              }}
            >
              <CircularProgress color="inherit" size={32} thickness={4} />
            </Box>
          </IconButton>
        </Box>
      </Box>
      {isPlaying && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Tooltip title={isLooping ? 'Repetă: activ' : 'Repetă: dezactivat'}>
            <Button
              onClick={() => setIsLooping((s) => !s)}
              aria-label={`Repetă cântecul ${isLooping ? 'activ' : 'dezactivat'}`}
              aria-pressed={isLooping}
              size="large"
              variant={isLooping ? 'contained' : 'contained'}
              color={isLooping ? 'primary' : 'inherit'}
              startIcon={<LoopIcon sx={{ transform: isLooping ? 'rotate(360deg)' : 'none', transition: 'transform 400ms' }} />}
              sx={{ textTransform: 'none', px: 2 }}
            >
              <Box component="span" sx={{ mr: 1 }}>Repetă cântecul</Box>
              <Box component="span" sx={{ fontSize: 12, opacity: 0.9, width: 28 }}>{isLooping ? 'ON' : 'OFF'}</Box>
            </Button>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

function useYouTubePlayer(
  isPlaying: boolean,
  videoId: string | undefined,
  containerRef: React.RefObject<HTMLDivElement | null>,
  playerRef: React.MutableRefObject<any | null>,
  lyricsName?: string,
  isLooping?: boolean,
  setIsPlaying?: (v: boolean) => void,
  setIsIframeVisible?: (v: boolean) => void,
) {
  const isLoopingRef = useRef<boolean>(!!isLooping);
  // Keep a live ref updated when `isLooping` changes so event handlers see latest value
  useEffect(() => {
    isLoopingRef.current = !!isLooping;
  }, [isLooping]);
  // Intentionally using a lazy effect to only load when playing
  // Implementation avoids imports of YT types to keep things simple
  useEffect(() => {
    if (!isPlaying) return;
    if (!videoId) return;

    let mounted = true;

    const createPlayer = () => {
      if (!containerRef.current) return;
      const YT = window.YT;
      if (!YT || !YT.Player) return;

      if (playerRef.current && playerRef.current.destroy) {
        try { playerRef.current.destroy(); } catch (e) { }
        playerRef.current = null;
      }

      playerRef.current = new YT.Player(containerRef.current, {
        height: '100%',
        width: '100%',
        videoId,
        playerVars: {
          autoplay: 1,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
        },
        events: {
          onReady: (e: any) => {
            try { e.target.playVideo(); } catch (_) { }
          },
          onStateChange: (e: any) => {
            const Y = window.YT;
            if (!Y) return;
            switch (e.data) {
              case Y.PlayerState.PLAYING:
                // only reveal the iframe when playback actually starts
                if (setIsIframeVisible) setIsIframeVisible(true);
                trackEvent(AnalyticsEvents.VERSURI_YOUTUBE_PLAY, {
                  source: 'youtube',
                  videoId,
                  title: lyricsName,
                });
                break;
              case Y.PlayerState.PAUSED:
                trackEvent(AnalyticsEvents.VERSURI_YOUTUBE_PAUSE, {
                  source: 'youtube',
                  videoId,
                  title: lyricsName,
                });
                break;
              case Y.PlayerState.ENDED:
                trackEvent(AnalyticsEvents.VERSURI_YOUTUBE_ENDED, {
                  source: 'youtube',
                  videoId,
                  title: lyricsName,
                });
                if (isLoopingRef.current) {
                  try { e.target.seekTo(0); e.target.playVideo(); } catch (_) { }
                } else {
                  if (mounted && setIsPlaying) setIsPlaying(false);
                  if (setIsIframeVisible) setIsIframeVisible(false);
                }
                break;
              default:
                break;
            }
          },
        },
      });
    };

    if (!(window).YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.async = true;
      document.body.appendChild(tag);

      (window as any).onYouTubeIframeAPIReady = () => {
        if (!mounted) return;
        createPlayer();
      };
    } else {
      createPlayer();
    }

    return () => {
      mounted = false;
      try {
        if (playerRef.current && playerRef.current.destroy) {
          playerRef.current.destroy();
          playerRef.current = null;
        }
      } catch (_) { }
      if (setIsIframeVisible) setIsIframeVisible(false);
    };
  }, [isPlaying, videoId, containerRef, playerRef, lyricsName, setIsPlaying, setIsIframeVisible]);
}

export default YoutubePlayer;